'use client'

import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { type ReactNode, useRef } from 'react'

/**
 * Lets its children drift against the page scroll while the block crosses the viewport.
 * Transform only, so it cannot shift the layout.
 *
 * prefers-reduced-motion is checked here instead of relying on <MotionConfig reducedMotion="user">:
 * that only governs animations, not a motion value fed by useTransform, so the drift has to be
 * dropped by hand.
 */
export function Parallax({
  children,
  distance = 32,
  className,
}: {
  children: ReactNode
  /** Pixels travelled between entering and leaving the viewport. Negative moves the other way. */
  distance?: number
  className?: string
}) {
  // The outer element is the measured target, the inner one carries the transform.
  const ref = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance])

  return (
    <div
      ref={ref}
      className={className}
    >
      <motion.div style={reduced ? undefined : { y }}>{children}</motion.div>
    </div>
  )
}
